/**
 * Centralized analytics constants for the snippets package.
 *
 * Organized into three sections:
 *   1. Shared    – source, event types, origins, page modes (used by all events)
 *   2. UI        – subjects, actions, subject IDs, modal outcomes (sendUIEvent)
 *   3. Operational – subjects, actions (sendOperationalEvent)
 */

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Shared
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Event type used to dispatch analytics events */
export enum ANALYTICS_EVENT_TYPE {
	SEND_UI_EVENT = 'sendUIEvent',
	SEND_OPERATIONAL_EVENT = 'sendOperationalEvent',
}

/** The data.type value inside an analytics event payload */
export enum ANALYTICS_TYPE {
	UI = 'ui',
	OPERATIONAL = 'operational',
}

/** Single source for all snippets analytics events */
export const ANALYTICS_SOURCE = 'confluenceSnippets';

/** Identifies which component/surface fired the event */
export enum ANALYTICS_ORIGIN {
	// snippet CRUD modals
	CREATE_SNIPPET_MODAL = 'createSnippetModal',
	EDIT_SNIPPET_MODAL = 'editSnippetModal',
	DUPLICATE_SNIPPET_MODAL = 'duplicateSnippetModal',
	DELETE_SNIPPET_MODAL = 'deleteSnippetModal',
	// onboarding
	FIRST_SNIPPET_CREATED_MODAL = 'firstSnippetCreatedModal',
	// surfaces
	SNIPPET_PANEL = 'snippetPanel',
	SNIPPET_GALLERY = 'snippetGallery',
	// toolbars / menus
	CONTEXTUAL_TOOLBAR = 'contextualToolbar',
	BLOCK_MENU = 'blockMenu',
}

export type AnalyticsOrigin = ANALYTICS_ORIGIN;

/** Identifies the page mode when the analytics event was fired */
export enum ANALYTICS_PAGE_MODE {
	VIEW = 'view',
	EDIT = 'edit',
	LIVE = 'live',
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  UI events (sendUIEvent)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Action subjects – the type of UI element (e.g. button, modal) */
export enum ANALYTICS_UI_SUBJECT {
	BUTTON = 'button',
	MODAL = 'modal',
	TEXT_FIELD = 'textField',
	DROPDOWN_ITEM = 'dropdownItem',
	SNIPPETS = 'snippets',
}

/** Actions – the user interaction that occurred */
export enum ANALYTICS_UI_ACTION {
	CLICKED = 'clicked',
	CLOSED = 'closed',
	TYPED = 'typed',
}

/** Action subject IDs – the specific UI element involved */
export enum ANALYTICS_UI_SUBJECT_ID {
	// button
	CREATE_SNIPPET = 'createSnippet',
	DELETE_SNIPPET = 'deleteSnippet',
	EDIT_SNIPPET = 'editSnippet',
	DUPLICATE_SNIPPET = 'duplicateSnippet',
	INSERT_SNIPPET = 'insertSnippet',
	SNIPPET_FAVORITE_TOGGLED = 'snippetFavoriteToggled',
	// textField / button / dropdownItem (filter-related)
	SNIPPET_CATEGORY_FILTER = 'snippetCategoryFilter',
	SNIPPET_SEARCH = 'snippetSearch',
	SNIPPET_SORT_ORDER = 'snippetSortOrder',
	// FirstSnippetCreatedModal (use attributes for viewed vs dismissed and dismissalAction)
	FIRST_SNIPPET_CREATED_MODAL = 'firstSnippetCreatedModal',
	// Empty state primary actions
	EMPTY_MY_TEMPLATES_CALL_TO_ACTION = 'emptyMyTemplatesCallToAction',
	EMPTY_FAVORITES_CALL_TO_ACTION = 'emptyFavoritesCallToAction',
}

/** Outcome values for modal analytics events (passed in attributes.outcome) */
export enum MODAL_OUTCOME {
	CREATED = 'created',
	EDITED = 'edited',
	DUPLICATED = 'duplicated',
	DELETED = 'deleted',
	CANCELLED = 'cancelled',
	CLOSED = 'closed',
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
//  Operational events (sendOperationalEvent)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

/** Action subjects for operational events */
export enum ANALYTICS_OPERATIONAL_SUBJECT {
	SNIPPET_FAVORITE_TOGGLED = 'snippetFavoriteToggled',
	INSERT_SNIPPET = 'insertSnippet',
	// API failures
	SNIPPET_API_CREATE = 'snippetApiCreate',
	SNIPPET_API_DUPLICATE = 'snippetApiDuplicate',
	SNIPPET_API_UPDATE = 'snippetApiUpdate',
	SNIPPET_API_DELETE = 'snippetApiDelete',
	SNIPPET_API_FETCH = 'snippetApiFetch',
	SNIPPET_API_SHARE = 'snippetApiShare',
	SNIPPET_API_UNSHARE = 'snippetApiUnshare',
	// Client-side errors
	SNIPPET_ADF_SERIALIZATION = 'snippetAdfSerialization',
	SNIPPET_PREVIEW_RENDER = 'snippetPreviewRender',
}

/** Actions for operational events */
export enum ANALYTICS_OPERATIONAL_ACTION {
	FAILED = 'failed',
	FETCHED = 'fetched',
}
